import classNames from 'classnames';
import { usePathname } from '@/navigation';

import {
  CartItemProperty,
  ITEM_ORIGINS,
  TW_PRODUCT_MERCH_CARD_CLASSES,
} from '@/core/constants';
import { HeaderText, LuvImage } from '@/components/ui';
import { HeaderSize } from '@/components/ui/HeaderText';
import {
  PRODUCT_ADDED_COMPONENT_TYPES,
  PRODUCT_ADDED_PAGE_TYPES,
  PRODUCT_EVENT_TYPES,
  trackProductEvent,
} from '@/utils/rudderstack';
import {
  ShopifyProduct,
  ShopifyProductType,
  ShopifyVariant,
} from '@/lib/shopify';
import { captureProductFieldsEvent, pushEvent } from '@/utils/dataLayer';
import { TypeProductFields } from '@/core/generated-types';
import { API_URLS } from '@/lib/api/config';

import { InternalLinkWrapper } from '../../InternalLinkWrapper';
import { ExtendedProductMerchCardProps } from '../ProductMerchCard';
import { AddToCartCta } from '../../AddToCartCta';
import { slugBuilder } from '@/core/contentfulConfig';

function CartCtaCard({
  product,
  includes,
  includesLabel,
  productWithAddonsPricePrefix,
  pdpLink,
}: ExtendedProductMerchCardProps) {
  const pathname = usePathname();

  const {
    featuredImage,
    title,
    shortDescription,
    productAddOns,
    shopifyProduct,
    ageRange,
  } = product.fields;

  const isCoursePack =
    shopifyProduct?.productType === ShopifyProductType.COURSE;
  const isSkillSet =
    shopifyProduct?.productType === ShopifyProductType.SKILL_SET;
  const productHasAddons = productAddOns && productAddOns.length > 0;

  let pricingCopyMaybeWithPrefix = '';
  if (shopifyProduct) {
    const productPrice = shopifyProduct.variants[0].formattedPrice || '';
    pricingCopyMaybeWithPrefix =
      productHasAddons && productWithAddonsPricePrefix
        ? `${productWithAddonsPricePrefix} ${productPrice}`
        : productPrice;
  }

  const onProductClick = () => {
    const payload = {
      url: pathname || '',
      destinationUrl: slugBuilder(pdpLink) || '',
      productId: shopifyProduct?.id || '',
      sku: shopifyProduct?.variants[0].sku || '',
      category: shopifyProduct?.productType || '',
      name: title,
      ageRange: ageRange || '',
      ageUnits: '',
      price: shopifyProduct?.variants[0].price?.amount || 0,
      quantity: 1,
    };

    if (shopifyProduct) {
      trackProductEvent(PRODUCT_EVENT_TYPES.PRODUCT_CLICKED, payload);
    }
  };

  const handleFormSubmit = (
    e: React.FormEvent<HTMLFormElement>,
    shopifyProduct: ShopifyProduct,
    variant: ShopifyVariant,
    productFields: TypeProductFields,
    url: string,
    qtyVal: number,
  ) => {
    try {
      e.preventDefault();

      pushEvent('addToCart', { qtyVal, shopifyProduct, variant });
      captureProductFieldsEvent(
        PRODUCT_EVENT_TYPES.PRODUCT_ADDED,
        {
          shopifyProduct,
          variant,
          product: productFields,
          quantity: qtyVal,
          url,
        },
        PRODUCT_ADDED_COMPONENT_TYPES.ROBUST_PRODUCT_CARD,
        PRODUCT_ADDED_PAGE_TYPES.PRODUCT_DETAIL_PAGE,
      );

      e.currentTarget.submit();
    } catch (error) {
      console.error('Error tracking ATC event', error);
      e.currentTarget.submit();
    }
  };

  return (
    <div
      className={classNames(
        'rounded bg-white drop-shadow-md flex flex-col',
        TW_PRODUCT_MERCH_CARD_CLASSES.container,
      )}
    >
      <InternalLinkWrapper link={pdpLink} onClick={onProductClick}>
        <figure className="rounded-t">
          <LuvImage
            height={240}
            width={360}
            imageData={featuredImage}
            alt={featuredImage?.fields?.altText || ''}
            className={classNames(
              'w-full rounded-t',
              TW_PRODUCT_MERCH_CARD_CLASSES.image,
            )}
          />
        </figure>
        <div className="flex grow flex-col mx-4 my-6">
          <HeaderText size={HeaderSize.H3} text={title} align="left" />
          <p className="text-base text-left mt-2 mb-6">{shortDescription}</p>
          {includes && (
            <div
              className="md:min-h-[224px]"
              data-testid="product-includes-container"
            >
              <p className="text-left font-bold mb-4">{includesLabel}</p>
              {includes.map((iconAndShortDescription) => (
                <div
                  className="mt-2 flex items-start gap-3 mx-4"
                  key={iconAndShortDescription.sys.id}
                >
                  <LuvImage
                    height={30}
                    width={30}
                    imageData={iconAndShortDescription.fields.image}
                    className="object-none w-3 pt-1"
                    alt={iconAndShortDescription.fields.image.fields?.altText}
                  />
                  <p className="text-lg">
                    {iconAndShortDescription.fields.description}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </InternalLinkWrapper>
      {shopifyProduct && (
        <div className="flex grow flex-col mx-4 mb-6">
          <HeaderText
            size={HeaderSize.H3}
            text={pricingCopyMaybeWithPrefix}
            align="left"
            classes="mt-auto mb-4"
          />
          <form
            className="*:w-full"
            id={`add-to-cart-form-${shopifyProduct.handle}`}
            data-testid={`add-to-cart-form-${shopifyProduct.handle}`}
            action={`${API_URLS.CART_SERVICE}/add`}
            method="post"
            onSubmit={(e) =>
              handleFormSubmit(
                e,
                shopifyProduct,
                shopifyProduct.variants[0],
                product.fields,
                pathname || '',
                1,
              )
            }
          >
            <input
              type="hidden"
              name="lines"
              value={JSON.stringify([
                {
                  merchandiseId: shopifyProduct.variants[0].id,
                  quantity: 1,
                  attributes: [
                    {
                      key: CartItemProperty.QuantityLimitedToOne,
                      value: String(isCoursePack || isSkillSet),
                    },
                    {
                      key: CartItemProperty.IsCoursePack,
                      value: String(isCoursePack),
                    },
                    {
                      key: CartItemProperty.IsSkillSet,
                      value: String(isSkillSet),
                    },
                    {
                      key: CartItemProperty.ItemOrigin,
                      value: ITEM_ORIGINS.CUSTOMER_PORTAL,
                    },
                  ],
                },
              ])}
            />
            <AddToCartCta
              addToCartText="Add to Cart"
              form={`add-to-cart-form-${shopifyProduct.handle}`}
            />
          </form>
        </div>
      )}
    </div>
  );
}

export default CartCtaCard;
